<template>
	<div v-if="context.raete" role="region" aria-label="Ratsmitglieder">
		<nk-wrapper
			v-for="(fraktion, fraktionIndex) in sortedRaete"
			:key="`fraktion-${fraktionIndex}`"
			spacing="lg"
		>
			<section>
				<nk-title size="xl" tag="h2">
					{{ fraktion.titel }}
				</nk-title>
				<nk-divider />
				<nk-row vertical-gutter="4">
					<nk-col
						v-for="(rat, ratIndex) in fraktion.raete"
						:key="`rat-${ratIndex}`"
						cols="full"
						sm="6/12"
						:md="!ratHasImage ? '4/12' : null"
					>
						<nk-row vertical-gutter="2" gutter="3">
							<nk-col v-if="ratHasImage" cols="full" lg="4/12" xl="4/12">
								<nk-link
									v-if="
										((rat.taetigkeitsbereiche[0].detailURL && rat.portrait) ||
											rat.taetigkeitsbereiche_alle.length) &&
										options.detail_link
									"
									:link="rat.taetigkeitsbereiche[0].detailURL"
								>
									<nk-image
										v-if="rat.bild"
										:src="rat.bild"
										:alt="rat.bildtext"
										:copyright="rat.bildcopyright"
										ar="1:1"
										image-class="w-full"
									/>
									<nk-image
										v-else
										src="/static-images/nk-layout/person-placeholder.png"
										:alt="`Platzhalterbild für ${rat.vorname} ${rat.nachname}`"
										ar="1:1"
										image-class="w-full"
									/>
								</nk-link>
								<nk-image
									v-else-if="rat.bild"
									:src="rat.bild"
									:alt="rat.bildtext"
									:copyright="rat.bildcopyright"
									ar="1:1"
									image-class="w-full"
								/>
								<nk-image
									v-else
									src="/static-images/nk-layout/person-placeholder.png"
									:alt="`Platzhalterbild für ${rat.vorname} ${rat.nachname}`"
									ar="1:1"
									image-class="w-full"
								/>
							</nk-col>
							<nk-col cols="full" lg="auto">
								<nk-link
									v-if="
										((rat.taetigkeitsbereiche[0].detailURL && rat.portrait) ||
											rat.taetigkeitsbereiche_alle.length) &&
										options.detail_link
									"
									:link="rat.taetigkeitsbereiche[0].detailURL"
								>
									<nk-title size="lg" tag="h3" no-margin>
										{{ rat.vorname }} {{ rat.nachname }}
									</nk-title>
								</nk-link>
								<nk-title v-else size="lg" tag="h3" no-margin>
									{{ rat.vorname }} {{ rat.nachname }}
								</nk-title>
								<nk-text v-if="getFunktion(rat.taetigkeitsbereiche)">
									{{ getFunktion(rat.taetigkeitsbereiche) }}
								</nk-text>
								<nk-wrapper spacing="sm">
									<nk-list v-if="rat.taetigkeitsbereiche_alle.length > 1">
										<template
											v-for="(
												taetigkeitsbereich, taetigkeitsbereicheIndex
											) in rat.taetigkeitsbereiche_alle"
										>
											<template
												v-if="
													taetigkeitsbereich.kategorie != null &&
													taetigkeitsbereich.kategorie !==
														options.haupttaetigkeit
												"
											>
												<nk-list-item
													:key="`taetigkeitsbereiche-${taetigkeitsbereicheIndex}`"
												>
													{{ taetigkeitsbereich.taetigkeitsbereiche }}
													<template
														v-if="
															taetigkeitsbereich.funktion != null &&
															taetigkeitsbereich.funktion.length > 0
														"
													>
														<br />
														({{ taetigkeitsbereich.funktion }})
													</template>
												</nk-list-item>
											</template>
										</template>
									</nk-list>
								</nk-wrapper>
								<nk-address
									variant="vertical"
									v-bind="{...rat.kontakt, ...rat.adresse}"
									:hide-anfahrt="hideAnfahrt"
								/>
							</nk-col>
						</nk-row>
					</nk-col>
				</nk-row>
			</section>
		</nk-wrapper>
	</div>
</template>

<script>
import {groupBy, map} from 'lodash-es';
import TemplatesDefault from '../../mixins/templates-default';

/**
 * @prop {array} context.raete
 * @prop {boolean} options.hideAnfahrt
 * @prop {boolean} options.showFunktion
 * @prop {boolean} options.showTaetigkeitsbereiche
 * @prop {boolean} options.haupttaetigkeit
 */
export default {
	name: 'NkTemplateRaeteUebersicht',

	mixins: [TemplatesDefault],

	computed: {
		sortedRaete() {
			const group = groupBy(this.context.raete, 'fraktion');
			return map(group, (value, key) => ({titel: key, raete: value}));
		},
		ratHasImage() {
			return this.context.raete.some(rat => rat.bild);
		},
		hideAnfahrt() {
			return this.options &&
				Object.prototype.hasOwnProperty.call(this.options, 'hideAnfahrt')
				? this.options.hideAnfahrt
				: null;
		},
		showFunktion() {
			return this.options &&
				Object.prototype.hasOwnProperty.call(this.options, 'showFunktion')
				? this.options.showFunktion
				: true;
		},
		showFunktionOfKategorie() {
			return this.options &&
				Object.prototype.hasOwnProperty.call(
					this.options,
					'showFunktionOfKategorie',
				)
				? this.options.showFunktionOfKategorie
				: null;
		},
	},
	methods: {
		getFunktion(taetigkeitsbereiche) {
			if (!this.showFunktion) {
				return null;
			}

			// filter array taetigkeitsbereiche where kategorie is not null
			taetigkeitsbereiche = taetigkeitsbereiche.filter(
				taetigkeitsbereich =>
					taetigkeitsbereich.kategorie != null &&
					taetigkeitsbereich.kategorie === this.options.haupttaetigkeit,
			);

			if (taetigkeitsbereiche.length === 0) {
				return null;
			}

			let funktion = taetigkeitsbereiche[0].funktion;

			if (this.showFunktionOfKategorie) {
				const taetigkeitsbereich = taetigkeitsbereiche.find(
					t => t.kategorie === this.showFunktionOfKategorie,
				);

				if (taetigkeitsbereich) {
					funktion = taetigkeitsbereich.funktion;
				}
			}

			return funktion;
		},
		getAusschuss(taetigkeitsbereiche) {
			return taetigkeitsbereiche.find(t => t.kategorie === 'ausschuss');
		},
	},
};
</script>
